import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ChevronLeft,
  Users,
  Trophy,
  Loader2,
  MapPin,
  Play,
  Eye,
  Search,
  Calendar,
  Video,
  User,
  Shield,
  CheckCircle,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamProfile = () => {
  const { teamName } = useParams();
  const { token, user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [gameFilter, setGameFilter] = useState('all');
  const [gameSearch, setGameSearch] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/team-profile/${encodeURIComponent(teamName)}`);
      if (res.ok) setProfile(await res.json());
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [teamName]);

  useEffect(() => {
    setLoading(true);
    fetchProfile();
    // Check follow status
    if (token && teamName) {
      fetch(`${API_URL}/api/user/followed-teams/names`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []).then(names => {
        if (names.includes(decodeURIComponent(teamName))) setIsFollowing(true);
      }).catch(() => {});
    }
  }, [fetchProfile, token, teamName]);

  const toggleFollow = async () => {
    if (!isAuthenticated()) {
      toast.error('Sign in to follow teams');
      return;
    }
    const decoded = decodeURIComponent(teamName);
    try {
      if (isFollowing) {
        await fetch(`${API_URL}/api/user/followed-teams/${encodeURIComponent(decoded)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setIsFollowing(false);
        toast.success(`Unfollowed ${decoded}`);
      } else {
        await fetch(`${API_URL}/api/user/followed-teams`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ team_name: decoded }),
        });
        setIsFollowing(true);
        toast.success(`Following ${decoded}`);
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleClaim = async () => {
    if (!token) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/team-profile/${encodeURIComponent(teamName)}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Team claimed! You are now the program director for this team.');
        fetchProfile();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to claim team');
      }
    } catch {
      toast.error('Network error');
    }
    setClaiming(false);
  };

  const isCoach = user?.role === 'coach' || user?.role === 'admin' || user?.role === 'super_admin';
  const isClaimed = !!profile?.claimed_by;
  const isMyTeam = isClaimed && profile?.claimed_by?.id === user?.id;

  const filteredGames = useMemo(() => {
    if (!profile?.games) return [];
    let games = profile.games;
    if (gameFilter === 'upcoming') games = games.filter(g => g.status === 'scheduled' || g.status === 'upcoming');
    else if (gameFilter === 'past') games = games.filter(g => g.status === 'final' || g.status === 'completed' || g.status === 'ended');
    else if (gameFilter === 'live') games = games.filter(g => g.status === 'live' || g.status === 'halftime');
    if (gameSearch) {
      const q = gameSearch.toLowerCase();
      games = games.filter(g =>
        g.home_team_name?.toLowerCase().includes(q) ||
        g.away_team_name?.toLowerCase().includes(q) ||
        g.event_name?.toLowerCase().includes(q)
      );
    }
    return games;
  }, [profile, gameFilter, gameSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-rooam-blue animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Team Not Found</h2>
          <p className="text-gray-400 mb-4">No games found for "{decodeURIComponent(teamName)}"</p>
          <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black" asChild>
            <Link to="/events">Browse Events</Link>
          </Button>
        </div>
      </div>
    );
  }

  const upcomingCount = profile.games.filter(g => g.status === 'scheduled' || g.status === 'upcoming').length;
  const liveCount = profile.games.filter(g => g.status === 'live' || g.status === 'halftime').length;
  const pastCount = profile.games.filter(g => g.status === 'final' || g.status === 'completed' || g.status === 'ended').length;

  return (
    <div className="min-h-screen bg-rooam-black">
      {/* Hero Banner */}
      <div
        className="relative h-48 md:h-56"
        style={{ background: `linear-gradient(135deg, ${profile.color}40, ${profile.color}10, transparent)` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-rooam-black to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        {/* Back Link */}
        <Link to="/events" className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-4" data-testid="back-link">
          <ChevronLeft className="h-4 w-4" /> Back to Events
        </Link>

        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row gap-5 items-start mb-6">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-black border-4 shrink-0"
            style={{ backgroundColor: profile.color, borderColor: `${profile.color}80` }}
            data-testid="team-avatar"
          >
            {profile.name?.charAt(0)}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-black text-white" data-testid="team-name">
              {profile.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-400">
              <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue capitalize">
                {profile.sport}
              </Badge>
              {profile.record && profile.record !== '0-0' && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500">
                  {profile.record}
                </Badge>
              )}
              <span>{profile.total_games} games</span>
              {profile.events?.length > 0 && (
                <span>{profile.events.length} event{profile.events.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            {/* Claimed / Coach info */}
            {isClaimed && (
              <div className="flex items-center gap-2 mt-3">
                <Badge className="bg-green-500/10 text-green-400 border-green-500 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Managed by {isMyTeam ? 'you' : profile.claimed_by?.name}
                </Badge>
              </div>
            )}
          </div>
          {/* Follow & Claim Buttons */}
          <div className="shrink-0 flex flex-col gap-2">
            <Button
              onClick={toggleFollow}
              variant={isFollowing ? "outline" : "default"}
              className={isFollowing 
                ? "border-cyan-500 text-cyan-400 hover:bg-cyan-950 hover:text-cyan-300" 
                : "bg-cyan-600 hover:bg-cyan-500 text-white"
              }
              data-testid="follow-team-btn"
            >
              {isFollowing ? (
                <><UserMinus className="h-4 w-4 mr-2" />Following</>
              ) : (
                <><UserPlus className="h-4 w-4 mr-2" />Follow</>
              )}
            </Button>
            {!isClaimed && !isMyTeam && (
              isAuthenticated() && isCoach ? (
                <Button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                  data-testid="claim-team-btn"
                >
                  {claiming ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Claim This Team
                </Button>
              ) : (
                <Button
                  className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                  data-testid="claim-team-btn"
                  asChild
                >
                  <Link to={isAuthenticated() ? '/dashboard' : '/login'}>
                    <Shield className="h-4 w-4 mr-2" />
                    Claim This Team
                  </Link>
                </Button>
              )
            )}
            {isAuthenticated() && isMyTeam && (
              <Badge className="bg-rooam-blue/10 text-rooam-blue border-rooam-blue text-xs px-3 py-1.5">
                <Shield className="h-3 w-3 mr-1" />
                Your Team
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="games" className="space-y-4">
          <TabsList className="bg-rooam-gray-medium border border-rooam-gray-light w-full justify-start">
            <TabsTrigger value="athletes" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-sm">
              <Users className="h-4 w-4 mr-1" /> Athletes
            </TabsTrigger>
            <TabsTrigger value="games" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-sm">
              <Trophy className="h-4 w-4 mr-1" /> Games
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-sm">
              <Calendar className="h-4 w-4 mr-1" /> Events
            </TabsTrigger>
          </TabsList>

          {/* Athletes Tab */}
          <TabsContent value="athletes">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Users className="h-5 w-5 text-rooam-blue" />
                  Athletes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.athletes?.length > 0 ? (
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {profile.athletes.map((a) => (
                      <Link
                        key={a.id}
                        to={`/athlete/${a.id}`}
                        className="flex items-center gap-3 p-3 bg-rooam-black/50 rounded-lg border border-rooam-gray-light hover:border-rooam-blue transition-all"
                        data-testid={`athlete-card-${a.id}`}
                      >
                        <img
                          src={a.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${a.id}`}
                          alt={a.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{a.name}</p>
                          <p className="text-xs text-gray-400">{a.position} {a.number ? `#${a.number}` : ''}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <User className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No athletes linked yet</p>
                    <p className="text-gray-600 text-xs mt-1">
                      Athletes can link their profiles by claiming roster spots on game pages
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Games Tab */}
          <TabsContent value="games">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 overflow-x-auto">
                    {[
                      { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
                      { key: 'live', label: 'Now', count: liveCount },
                      { key: 'past', label: 'Past Games', count: pastCount },
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setGameFilter(gameFilter === f.key ? 'all' : f.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                          gameFilter === f.key
                            ? 'bg-white text-black'
                            : 'bg-rooam-black/50 text-gray-400 hover:text-white'
                        }`}
                        data-testid={`filter-${f.key}`}
                      >
                        <span className={`text-xs font-bold ${gameFilter === f.key ? 'text-black' : 'text-gray-500'}`}>
                          {f.count}
                        </span>
                        {f.key === 'live' && liveCount > 0 && (
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        )}
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative sm:ml-auto sm:w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                    <Input
                      placeholder="Search games..."
                      value={gameSearch}
                      onChange={(e) => setGameSearch(e.target.value)}
                      className="bg-rooam-black border-rooam-gray-light text-white pl-8 h-8 text-sm placeholder-gray-500"
                      data-testid="game-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {filteredGames.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No games found</p>
                ) : (
                  filteredGames.map((g) => {
                    const isHome = g.is_home;
                    const myScore = isHome ? g.score?.home : g.score?.away;
                    const oppScore = isHome ? g.score?.away : g.score?.home;
                    const oppName = isHome ? g.away_team_name : g.home_team_name;
                    const isFinal = g.status === 'final' || g.status === 'completed' || g.status === 'ended';
                    const won = isFinal && (myScore || 0) > (oppScore || 0);
                    const lost = isFinal && (oppScore || 0) > (myScore || 0);

                    return (
                      <Link
                        key={g.game_id}
                        to={`/game-live/${g.game_id}`}
                        className="block bg-rooam-black/50 rounded-lg border border-rooam-gray-light hover:border-rooam-blue/50 transition-all overflow-hidden"
                        data-testid={`game-card-${g.game_id}`}
                      >
                        {g.thumbnail_url && (
                          <div className="relative h-36 bg-zinc-900">
                            <img src={g.thumbnail_url} alt="" className="w-full h-full object-cover opacity-80" />
                            {(g.status === 'final' || g.video_url) && (
                              <Badge className="absolute top-2 left-2 bg-zinc-900/80 text-white text-xs">REPLAY</Badge>
                            )}
                            {g.status === 'live' && (
                              <Badge className="absolute top-2 left-2 bg-red-600 text-white animate-pulse text-xs">LIVE</Badge>
                            )}
                          </div>
                        )}
                        <div className="p-4">
                          {g.event_name && (
                            <p className="text-xs text-gray-500 mb-1">{g.event_name}</p>
                          )}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">{g.home_team_name}</span>
                                <span className="text-gray-600 text-sm">vs</span>
                                <span className="text-white font-medium">{g.away_team_name}</span>
                              </div>
                              {isFinal && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`font-bold ${won ? 'text-green-400' : lost ? 'text-red-400' : 'text-gray-400'}`}>
                                    {myScore || 0}
                                  </span>
                                  <span className="text-gray-600">-</span>
                                  <span className={`font-bold ${lost ? 'text-green-400' : won ? 'text-red-400' : 'text-gray-400'}`}>
                                    {oppScore || 0}
                                  </span>
                                  {won && <Badge className="bg-green-500/20 text-green-400 border-green-500 text-[10px]">W</Badge>}
                                  {lost && <Badge className="bg-red-500/20 text-red-400 border-red-500 text-[10px]">L</Badge>}
                                </div>
                              )}
                            </div>
                            {!isFinal && (
                              <Badge className={`text-xs ${g.status === 'live' ? 'bg-red-600 text-white' : 'bg-rooam-blue/20 text-rooam-blue border-rooam-blue'}`}>
                                {g.status === 'live' ? 'LIVE' : 'Upcoming'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            {g.venue_name && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {g.venue_name}
                              </span>
                            )}
                            {g.scheduled_at && (
                              <span>
                                {new Date(g.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-rooam-blue" /> Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.events?.length > 0 ? (
                  <div className="space-y-3">
                    {profile.events.map((ev) => (
                      <Link
                        key={ev.id}
                        to={`/event/${ev.id}`}
                        className="flex items-center gap-4 p-4 bg-rooam-black/50 rounded-lg border border-rooam-gray-light hover:border-rooam-blue/50 transition-all"
                      >
                        <div className="w-12 h-12 bg-rooam-blue/20 rounded-lg flex items-center justify-center shrink-0">
                          <Trophy className="h-6 w-6 text-rooam-blue" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{ev.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            {ev.location && <span>{ev.location}</span>}
                            {ev.date && (
                              <span>
                                {new Date(ev.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No events found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Spacer */}
      <div className="h-16" />
    </div>
  );
};

export default TeamProfile;
